import { Artwork } from '../types/Artwork';
import { ArtworkMatch } from '../types/ArtworkMatch';
import { GalleryConfig } from '../types/GalleryConfig';
import { GallerySection } from '../types/GallerySection';
import { GalleryOrderKey } from '../types/GalleryOrder';
import { NavNode, NavNodeKind } from '../types/NavNode';

// Pure resolvers that turn GALLERY_CONFIG + CATALOG (configuration) into rendered data.
// No React, no side effects — unit-tested in galleryResolver.test.ts. Kept in util/ to
// separate functionality from configuration.

// The gallery-type key a leaf section is ordered by — the single dimension of its `match`
// (Featured tag > series > year, in that priority). undefined for branches and for leaves
// defined by an explicit `artworkIds` list or by medium only; those keep their natural order
// (the artworkIds order, or catalog order), since Artwork.order carries no key for them.
const orderKeyForSection = (section: GallerySection): GalleryOrderKey | undefined => {
    const m = section.match;
    if (!m) return undefined;
    if (m.tags && m.tags.length) return m.tags[0];
    if (m.series) return m.series;
    if (m.years && m.years.length) return m.years[0];
    return undefined;
};

// An artwork's position within a given gallery type; pieces without an entry sort to the end.
const orderValue = (a: Artwork, key: GalleryOrderKey | undefined): number =>
    key !== undefined && a.order[key] != null ? (a.order[key] as number) : Number.MAX_SAFE_INTEGER;

const kindOf = (section: GallerySection): NavNodeKind =>
    section.children && section.children.length ? 'branch' : section.href ? 'link' : 'gallery-leaf';

/** Does an artwork satisfy a match? AND across fields; OR within each field's array. */
export const matchesArtwork = (artwork: Artwork, match: ArtworkMatch): boolean => {
    if (match.years && !match.years.includes(artwork.year)) return false;
    if (match.mediums && !match.mediums.includes(artwork.medium)) return false;
    if (match.series && artwork.series !== match.series) return false;
    if (match.tags && !match.tags.some((t) => artwork.tags?.includes(t))) return false;
    return true;
};

/**
 * Artworks belonging to a section, sorted by the section's gallery-type order:
 *  - branch      → de-duped union of all descendant leaves
 *  - gallery leaf → explicit `artworkIds` ∪ `match` results
 *  - link leaf   → []
 * Ordering uses each piece's `order[<gallery type>]`; pieces without an entry for this
 * gallery keep their collection order (stable sort). Artworks with `included === false`
 * are excluded everywhere.
 */
export const resolveMembership = (section: GallerySection, catalog: Artwork[]): Artwork[] => {
    const pool = catalog.filter((a) => a.included);

    const collect = (s: GallerySection): Artwork[] => {
        if (s.children && s.children.length) return s.children.flatMap(collect);
        if (s.href) return [];
        const out: Artwork[] = [];
        if (s.artworkIds) {
            for (const ref of s.artworkIds) {
                const found = pool.find((a) => a.id === ref || a.slug === ref);
                if (found) out.push(found);
            }
        }
        if (s.match) {
            for (const a of pool) if (matchesArtwork(a, s.match)) out.push(a);
        }
        return out;
    };

    const byId = new Map<string, Artwork>();
    for (const a of collect(section)) if (!byId.has(a.id)) byId.set(a.id, a);
    const key = orderKeyForSection(section);
    return Array.from(byId.values()).sort((a, b) => orderValue(a, key) - orderValue(b, key));
};

/** Count of artworks in a section (leaf count, or aggregate distinct count for a branch). */
export const countFor = (section: GallerySection, catalog: Artwork[]): number =>
    resolveMembership(section, catalog).length;

/** Walk the config tree to the section at `slugPath`; undefined if any segment is missing. */
export const findSectionByPath = (
    config: GalleryConfig,
    slugPath: string[],
): GallerySection | undefined => {
    let sections: GallerySection[] = config.sections;
    let current: GallerySection | undefined;
    for (const slug of slugPath) {
        current = sections.find((s) => s.slug === slug);
        if (!current) return undefined;
        sections = current.children ?? [];
    }
    return current;
};

/** Resolved membership for a slug path; [] if the path resolves to no section. */
export const membershipByPath = (
    config: GalleryConfig,
    catalog: Artwork[],
    slugPath: string[],
): Artwork[] => {
    const section = findSectionByPath(config, slugPath);
    return section ? resolveMembership(section, catalog) : [];
};

/** Labels along `slugPath`, for the breadcrumb trail. Stops at the first missing segment. */
export const breadcrumbFor = (config: GalleryConfig, slugPath: string[]): string[] => {
    const labels: string[] = [];
    let sections: GallerySection[] = config.sections;
    for (const slug of slugPath) {
        const section = sections.find((s) => s.slug === slug);
        if (!section) break;
        labels.push(section.label);
        sections = section.children ?? [];
    }
    return labels;
};

/** Look up an artwork by slug (or id). */
export const getArtwork = (slug: string, catalog: Artwork[]): Artwork | undefined =>
    catalog.find((a) => a.slug === slug || a.id === slug);

/**
 * The slug path of a section's first gallery-leaf descendant — used to redirect a bare
 * branch route (e.g. /paintings) to a renderable leaf (e.g. /paintings/featured).
 * Returns undefined if the section has no gallery leaf beneath it.
 */
export const firstLeafPath = (section: GallerySection, basePath: string[]): string[] | undefined => {
    if (!section.children || !section.children.length) {
        return section.href ? undefined : basePath; // gallery leaf → this path; link leaf → none
    }
    for (const child of section.children) {
        const found = firstLeafPath(child, [...basePath, child.slug]);
        if (found) return found;
    }
    return undefined;
};

/**
 * The slug path of an artwork's *primary* section — the first gallery leaf (in config
 * pre-order) whose membership contains it. Used for the viewing room's SEO canonical so a
 * piece appearing in several sections has one canonical URL. Undefined if it's in none.
 */
export const primarySectionPath = (
    config: GalleryConfig,
    catalog: Artwork[],
    slug: string,
): string[] | undefined => {
    let result: string[] | undefined;
    const walk = (sections: GallerySection[], base: string[]) => {
        for (const section of sections) {
            if (result) return;
            const path = [...base, section.slug];
            if (section.children && section.children.length) {
                walk(section.children, path);
            } else if (!section.href && resolveMembership(section, catalog).some((a) => a.slug === slug || a.id === slug)) {
                result = path;
                return;
            }
        }
    };
    walk(config.sections, []);
    return result;
};

/**
 * Build the nav view-model (NavNode[]) from config + catalog. Gallery leaves carry a route
 * href and (when counts are enabled) a resolved count; branches carry children; link leaves
 * carry their explicit href. Renders both the desktop sidebar and the mobile drawer.
 */
export const buildNavTree = (config: GalleryConfig, catalog: Artwork[]): NavNode[] => {
    const build = (section: GallerySection, parentPath: string[]): NavNode => {
        const slugPath = [...parentPath, section.slug];
        const kind = kindOf(section);

        if (kind === 'branch') {
            return {
                label: section.label,
                slugPath,
                kind,
                children: section.children!.map((child) => build(child, slugPath)),
            };
        }
        if (kind === 'link') {
            return { label: section.label, slugPath, kind, href: section.href };
        }
        const showCount = section.showCount ?? config.showCounts;
        return {
            label: section.label,
            slugPath,
            kind,
            href: `/${slugPath.join('/')}`,
            count: showCount ? countFor(section, catalog) : undefined,
        };
    };
    return config.sections.map((section) => build(section, []));
};
